import {
  File,
  FileArchive,
  FileAudio,
  FileBadge,
  FileBraces,
  FileCode,
  FileCode2,
  FileImage,
  FileJson2,
  FilePlay,
  FileSpreadsheet,
  FileText,
  FileType,
  FileType2,
  Presentation,
  Table2,
  type LucideIcon,
} from 'lucide-react'
import archiveIcon from '../../assets/file-type-icons/zip-document.svg'
import audioIcon from '../../assets/file-type-icons/audio.svg'
import browserIcon from '../../assets/file-type-icons/browser.svg'
import cssIcon from '../../assets/file-type-icons/css.svg'
import csvIcon from '../../assets/file-type-icons/csv-document.svg'
import diskImageIcon from '../../assets/file-type-icons/disk-image.svg'
import gifIcon from '../../assets/file-type-icons/gif.svg'
import illustratorIcon from '../../assets/file-type-icons/illustrator.svg'
import imageIcon from '../../assets/file-type-icons/generic-image.svg'
import javaIcon from '../../assets/file-type-icons/java.svg'
import jsonIcon from '../../assets/file-type-icons/json.svg'
import markdownIcon from '../../assets/file-type-icons/markdown-document.svg'
import officeExcelIcon from '../../assets/file-type-icons/microsoft-excel.svg'
import officePowerPointIcon from '../../assets/file-type-icons/microsoft-powerpoint.svg'
import officeWordIcon from '../../assets/file-type-icons/microsoft-word.svg'
import pdfIcon from '../../assets/file-type-icons/pdf-document.svg'
import pythonIcon from '../../assets/file-type-icons/python.svg'
import rIcon from '../../assets/file-type-icons/r.svg'
import sqlIcon from '../../assets/file-type-icons/sql.svg'
import svgIcon from '../../assets/file-type-icons/svg.svg'
import textIcon from '../../assets/file-type-icons/text.svg'
import videoIcon from '../../assets/file-type-icons/video.svg'
import xmlIcon from '../../assets/file-type-icons/xml.svg'
import { classifyFile } from '../../lib/file-classification'
import { cn } from '../../lib/utils'

const FILE_TYPE_ICON_SOURCES: Readonly<Record<string, string>> = {
  html: browserIcon,
  htm: browserIcon,
  xhtml: browserIcon,

  doc: officeWordIcon,
  docx: officeWordIcon,
  docm: officeWordIcon,
  odt: officeWordIcon,
  rtf: officeWordIcon,
  xls: officeExcelIcon,
  xlsx: officeExcelIcon,
  xlsm: officeExcelIcon,
  xlsb: officeExcelIcon,
  ods: officeExcelIcon,
  ppt: officePowerPointIcon,
  pptx: officePowerPointIcon,
  pptm: officePowerPointIcon,
  pps: officePowerPointIcon,
  ppsx: officePowerPointIcon,
  odp: officePowerPointIcon,
  pdf: pdfIcon,

  txt: textIcon,
  log: textIcon,
  env: textIcon,
  ini: textIcon,
  cfg: textIcon,
  conf: textIcon,
  properties: textIcon,
  gitignore: textIcon,
  gitattributes: textIcon,
  editorconfig: textIcon,
  npmrc: textIcon,
  nvmrc: textIcon,
  md: markdownIcon,
  mdx: markdownIcon,
  markdown: markdownIcon,
  json: jsonIcon,
  jsonc: jsonIcon,
  json5: jsonIcon,
  xml: xmlIcon,
  yaml: xmlIcon,
  yml: xmlIcon,
  toml: xmlIcon,
  csv: csvIcon,
  tsv: csvIcon,
  css: cssIcon,
  scss: cssIcon,
  sass: cssIcon,
  less: cssIcon,

  svg: svgIcon,
  gif: gifIcon,
  png: imageIcon,
  jpg: imageIcon,
  jpeg: imageIcon,
  webp: imageIcon,
  bmp: imageIcon,
  ico: imageIcon,
  avif: imageIcon,
  heic: imageIcon,
  heif: imageIcon,
  tif: imageIcon,
  tiff: imageIcon,
  mp4: videoIcon,
  webm: videoIcon,
  m4v: videoIcon,
  mov: videoIcon,
  avi: videoIcon,
  mkv: videoIcon,
  ogv: videoIcon,
  mp3: audioIcon,
  wav: audioIcon,
  m4a: audioIcon,
  aac: audioIcon,
  flac: audioIcon,
  ogg: audioIcon,
  oga: audioIcon,
  opus: audioIcon,

  zip: archiveIcon,
  rar: archiveIcon,
  '7z': archiveIcon,
  tar: archiveIcon,
  gz: archiveIcon,
  tgz: archiveIcon,
  bz2: archiveIcon,
  xz: archiveIcon,
  zst: archiveIcon,
  py: pythonIcon,
  pyw: pythonIcon,
  pyi: pythonIcon,
  java: javaIcon,
  kt: javaIcon,
  kts: javaIcon,
  r: rIcon,
  rmd: rIcon,
  sql: sqlIcon,
  ai: illustratorIcon,
  eps: illustratorIcon,
  iso: diskImageIcon,
  dmg: diskImageIcon,
  img: diskImageIcon,
}

function getExtension(filePath: string): string {
  const withoutQuery = filePath.split(/[?#]/, 1)[0] ?? filePath
  const basename = withoutQuery.split(/[\\/]/).pop() ?? withoutQuery
  const lowerBasename = basename.toLowerCase()
  if (lowerBasename === '.env' || lowerBasename.startsWith('.env.')) return 'env'
  if (['.gitignore', '.gitattributes', '.editorconfig', '.npmrc', '.nvmrc'].includes(lowerBasename)) {
    return lowerBasename.slice(1)
  }
  if (['dockerfile', 'makefile', 'jenkinsfile', 'gemfile', 'rakefile', 'procfile'].includes(lowerBasename)) {
    return lowerBasename
  }
  const dotIndex = basename.lastIndexOf('.')
  if (dotIndex === -1 || dotIndex === 0) return ''
  return basename.slice(dotIndex + 1).toLowerCase()
}

export function getMarkdownFileLinkIcon(path: string): LucideIcon {
  return getFilePathIcon(path)
}

/** Return the bundled color SVG for a recognized file type. */
export function getFilePathIconSource(path: string): string | undefined {
  return FILE_TYPE_ICON_SOURCES[getExtension(path)]
}

/**
 * Return a compact file icon for any local path. Extension-specific overrides
 * keep visually distinct formats (HTML, Markdown, CSV, archives, audio, etc.)
 * useful even when the file is not currently previewable in-app.
 */
export function getFilePathIcon(path: string): LucideIcon {
  const ext = getExtension(path)

  if (['dockerfile', 'makefile', 'jenkinsfile', 'gemfile', 'rakefile'].includes(ext)) return FileCode
  if (ext === 'procfile') return FileType
  if (['html', 'htm', 'xhtml'].includes(ext)) return FileCode2
  if (['md', 'mdx', 'markdown'].includes(ext)) return FileType2
  if (['json', 'jsonc', 'json5'].includes(ext)) return FileJson2
  if (['yaml', 'yml', 'toml', 'xml'].includes(ext)) return FileBraces
  if (['csv', 'tsv'].includes(ext)) return Table2
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz'].includes(ext)) return FileArchive
  if (['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'oga', 'opus'].includes(ext)) return FileAudio

  const type = classifyFile(path).type

  switch (type) {
    case 'image':
      return FileImage
    case 'video':
      return FilePlay
    case 'code':
      return FileCode
    case 'json':
      return FileJson2
    case 'pdf':
      return FileBadge
    case 'text':
      return FileType
    case 'markdown':
      return FileType2
    case 'office': {
      if (['xls', 'xlsx', 'xlsm'].includes(ext)) return FileSpreadsheet
      if (['ppt', 'pptx', 'pptm', 'ppsx'].includes(ext)) return Presentation
      return FileText
    }
    default:
      return File
  }
}

export interface FilePathIconProps {
  path: string
  className?: string
}

/**
 * Render a bundled, full-color SVG for supported formats and fall back to a
 * neutral Lucide icon when this icon set has no dedicated asset.
 */
export function FilePathIcon({ path, className }: FilePathIconProps) {
  const source = getFilePathIconSource(path)

  if (source) {
    return (
      <img
        src={source}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={cn('shrink-0 object-contain pointer-events-none select-none', className)}
      />
    )
  }

  const Icon = getFilePathIcon(path)
  return <Icon aria-hidden="true" focusable="false" className={className} />
}

export function MarkdownFileLinkIcon({
  path,
  className,
}: {
  path: string
  className?: string
}) {
  return (
    <FilePathIcon
      path={path}
      className={cn(
        'mr-1 inline-block h-3.5 w-3.5 shrink-0 align-[-0.18em] text-muted-foreground/70 pointer-events-none select-none',
        className,
      )}
    />
  )
}
